import { isEmpty } from 'utils/string';
import { SubscriptionTypes } from 'utils/subscription';
import { StoreMgmtStates } from './adminTenant';
import { IdentityProviderType } from './identityProvider';
import { Settings } from './setting';

// Our own default lead source (Instant 27001). Still used to detect partner-originated trials in the
// welcome flow. The trial 'source' is no longer validated against a hardcoded list; the backend resolves
// a GUID-shaped source to a partner tenant (see TenantController.ApplyTrialSourceAsync).
export const LeadSources_Instant27001 = '7a2561e1-c2f4-496f-8983-360704dd1bce';

// Whether the current partner user may open (impersonate) a customer tenant.
// Mirrors the backend Tenant.CustomerAccessStatus enum and the PrincipalAuthValidator rules.
export enum CustomerAccessStatus {
  Allowed = 0,
  NoPaidLicenseAtPartner = 1,
  NoLicenseAtCustomer = 2,
}

export default class Tenant {
  id: string;

  parentId?: string;

  topLevelParentTenantId: string;

  //the directory the tenant signs in with: the provider and its own id for it (an Entra tenant id, a Workspace domain)
  identityProviderType: IdentityProviderType;

  providerTenantId?: string;

  name: string;

  created?: Date;

  totalLicenses: number;

  usedUserLicenses: number;

  usedMgMtLicenses: number;

  numberOfUsers: number;

  userLicenses: number;

  mgMtLicenses: number;

  storeMgmtState: number;

  onboardingState?: TenantOnboarding;

  subscriptionType: number;

  settings?: Settings;

  timeZoneCode?: string;

  color?: string;

  currentUserLicensed?: boolean;

  appLogo?: Blob;

  constructor(id: string, name: string) {
    this.id = id;
    this.topLevelParentTenantId = id; //default same as id
    this.identityProviderType = IdentityProviderType.Entra;
    this.name = name;
    this.totalLicenses = 0;
    this.usedUserLicenses = 0;
    this.usedMgMtLicenses = 0;
    this.numberOfUsers = 0;
    this.userLicenses = 0;
    this.mgMtLicenses = 0;
    this.storeMgmtState = StoreMgmtStates.Customer;
    this.subscriptionType = SubscriptionTypes.Basic;
  }

  getUsedLicenses(): number {
    return this.usedUserLicenses + this.usedMgMtLicenses;
  }

  getFreeLicenses(): number {
    return this.totalLicenses - this.getUsedLicenses();
  }

  getFreeUserLicenses(): number {
    return this.userLicenses - this.usedUserLicenses;
  }

  getFreeMgMtLicenses(): number {
    return this.mgMtLicenses - this.usedMgMtLicenses;
  }

  static isDescendentOf(child: Tenant, parentId: string | undefined, orgUnits: Tenant[]): boolean {
    if (parentId && child.parentId) {
      if (child.parentId === parentId) return true;
      let nextParent: Tenant | undefined = child;

      nextParent = orgUnits.find((o) => o.id === nextParent?.parentId);
      if (!nextParent) return false;

      return this.isDescendentOf(nextParent, parentId, orgUnits);
    }

    return false;
  }

  clone() {
    const newTenant = new Tenant(this.id, this.name);
    newTenant.parentId = this.parentId;
    newTenant.topLevelParentTenantId = this.topLevelParentTenantId;
    newTenant.identityProviderType = this.identityProviderType;
    newTenant.providerTenantId = this.providerTenantId;
    newTenant.totalLicenses = this.totalLicenses;
    newTenant.usedUserLicenses = this.usedUserLicenses;
    newTenant.usedMgMtLicenses = this.usedMgMtLicenses;
    newTenant.usedUserLicenses = this.usedUserLicenses;
    newTenant.numberOfUsers = this.numberOfUsers;
    newTenant.userLicenses = this.userLicenses;
    newTenant.mgMtLicenses = this.mgMtLicenses;
    newTenant.storeMgmtState = this.storeMgmtState;
    newTenant.subscriptionType = this.subscriptionType;
    newTenant.timeZoneCode = this.timeZoneCode;
    newTenant.color = this.color;

    return newTenant;
  }
}

//
// On-boarding
//
export enum TrialWelcomeIntent {
  IsFirstISO = 1,
  IsCertified = 2,
  IsConsultant = 3,
}

export class TenantOnboarding {
  packageIds: string[];

  leadSource?: string;

  /** The IANA zone id the browser reports, such as Europe/Amsterdam. */
  timeZoneCode?: string;

  onboardingSteps: TenantOnboardingSteps;

  subscriptionType?: SubscriptionTypes;

  constructor() {
    this.packageIds = [];
    this.onboardingSteps = new TenantOnboardingSteps();
  }

  clone() {
    const newTenantOnboarding = new TenantOnboarding();
    newTenantOnboarding.packageIds = [...this.packageIds];
    newTenantOnboarding.leadSource = this.leadSource;
    newTenantOnboarding.timeZoneCode = this.timeZoneCode;
    newTenantOnboarding.onboardingSteps = this.onboardingSteps.clone();
    newTenantOnboarding.subscriptionType = this.subscriptionType;

    return newTenantOnboarding;
  }
}

export class TenantOnboardingSteps {
  finished: Date | undefined;

  currentStep: number | undefined;

  intent: TrialWelcomeIntent | undefined;

  phone: string | undefined;

  languageIsSet: boolean | undefined;

  certifiedStandards: number[] | undefined;

  certifiedOtherStandards: string | undefined;

  newStandards: number[] | undefined;

  newOtherStandards: string | undefined;

  currentSystem: number | undefined;

  currentOtherSystem: string | undefined;

  currentSystemImprovement: string | undefined;

  wantsToCertify: boolean | undefined;

  startDate: Date | undefined;

  endDate: Date | undefined;

  projectDocument: string | undefined;

  dashboardName: string | undefined;

  dashboardWidgetTaskStatusName: string | undefined;

  dashboardWidgetTaskTagName: string | undefined;

  optInAI: boolean = false;

  dashboardId: number | undefined;

  previewPackageInstalled: boolean | undefined;

  projectMilestonesCreated: boolean | undefined;

  isCertified = (): boolean | undefined => {
    if (!this.certifiedStandards) return undefined;

    return this.certifiedStandards.length > 0 || !isEmpty(this.certifiedOtherStandards);
  };

  needNewStandards = (): boolean | undefined => {
    if (!this.newStandards) return undefined;

    return this.newStandards.length > 0 || !isEmpty(this.newOtherStandards);
  };

  clone() {
    const output = new TenantOnboardingSteps();

    output.certifiedStandards = this.certifiedStandards;
    output.certifiedOtherStandards = this.certifiedOtherStandards;
    output.newStandards = this.newStandards;
    output.newOtherStandards = this.newOtherStandards;
    output.wantsToCertify = this.wantsToCertify;
    output.currentSystem = this.currentSystem;
    output.currentOtherSystem = this.currentOtherSystem;
    output.currentSystemImprovement = this.currentSystemImprovement;
    output.startDate = this.startDate;
    output.endDate = this.endDate;
    output.projectDocument = this.projectDocument;
    output.dashboardName = this.dashboardName;
    output.dashboardWidgetTaskStatusName = this.dashboardWidgetTaskStatusName;
    output.dashboardWidgetTaskTagName = this.dashboardWidgetTaskTagName;
    output.languageIsSet = this.languageIsSet;
    output.optInAI = this.optInAI;
    output.dashboardId = this.dashboardId;
    output.previewPackageInstalled = this.previewPackageInstalled;
    output.projectMilestonesCreated = this.projectMilestonesCreated;
    output.currentStep = this.currentStep;
    output.intent = this.intent;
    output.phone = this.phone;
    output.finished = this.finished;

    return output;
  }
}
